import { useContext, useEffect, useState } from "react";
import { UseFormGetValues, UseFormSetValue, UseFormWatch } from "react-hook-form";

import useFetch from "../useFetch";
import { resetDoctorAndPatientDetails } from "src/helpers/addCase";

import { APIEndpoints } from "src/types/apiTypes";
import { AddCaseProps } from "src/pages/addCase/types";
import { PracticeDropdownResponse, ProviderDropdownResponse } from "src/components/caseStepTwo/types";
import { useDebouncedCallback } from "use-debounce";
import { AuthContext } from "src/context/authContext";



const useTreatingPracAndDocFunc = (
  getValues: UseFormGetValues<AddCaseProps>, watch: UseFormWatch<AddCaseProps>, 
  setValue: UseFormSetValue<AddCaseProps>
) => {
  const [firstTreatingPracticeId] = useState(getValues("doctor_attributes.treating_practice_id"));

  const { userData } = useContext(AuthContext)
  const iAm = watch("doctor_attributes.i_am")
  const treatingPracticeId = watch("doctor_attributes.treating_practice_id")
  const isAnesthesiologist = watch("doctor_attributes.is_anesthesiologist")
  const submittingPractice = watch("doctor_attributes.submitting_practice_id")



  const { data: defaultPracticeData } =
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, {}, {}, ["Practice", "Contact"].includes(userData?.ref_type || ""))



  const { loading: isPracAPILoading, data: practiceListing, mutate: getPracticeListing, removeErrorAndData: clearPracticeListing } =
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, {}, {}, false)

  const getListingBySearchQuery = (searchQuery: string, shouldCalledImmediately: boolean = false, practiceId: number = 0): void => {
    if (shouldCalledImmediately) {
      if (practiceId)
        getPracticeListing({}, {}, { practice_id: practiceId })
      else  
        getPracticeListing({}, {}, { search_query: searchQuery })
    }

    if (searchQuery.length >= 3) {
      getPracticeListing({}, {}, { search_query: searchQuery })
    }
  }

  const debouncedPracticeSearch = useDebouncedCallback(getListingBySearchQuery, 1500)




  const { loading: doctorPracLoading, data: doctorPracListing, mutate: getDoctorPracLisitng } =
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, {}, {}, false)

  const getDoctorPracListingByQuery = (searchQuery: string, shouldCalledImmediately?: boolean, practiceId?: number) => {
    if (shouldCalledImmediately) {
      if (practiceId)
        getDoctorPracLisitng({}, {}, { practice_id: practiceId })
      else  
        getDoctorPracLisitng({}, {}, { search_query: searchQuery })
    }

    if (searchQuery.length >= 3) {
      getDoctorPracLisitng({}, {}, { search_query: searchQuery })
    }
  }

  const getDebouncedDoctorPracListingByQuery = useDebouncedCallback(getDoctorPracListingByQuery, 1500)

  useEffect(() => {
    if (doctorPracListing && doctorPracListing.data && doctorPracListing.data.length > 0) {
      if (userData && ["Practice", "Contact"].includes(userData.ref_type) && ["Treating Provider"].includes(iAm)) {
        const selectedPrac = doctorPracListing.data[0].id;
        setValue("doctor_attributes.treating_doctor_id", selectedPrac)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorPracListing])




  const { data: globalDoctorsApiResponse, mutate: getGlobalDoctorsListing } =
    useFetch<ProviderDropdownResponse>(APIEndpoints.PROVIDER_DROPDOWN, {}, {}, false);

  const getDoctorListing = () => {
    const globalDoctorParams = {
      practice_id: 0,
    };
    if (["Organization"].includes(userData?.ref_type || "")) {
      if (["Treating Provider"].includes(iAm))
        globalDoctorParams.practice_id = submittingPractice;
      else
        globalDoctorParams.practice_id = treatingPracticeId;
    } else if (
      (treatingPracticeId === 0) || 
      (["Treating Provider"].includes(iAm) && ["Practice", "Contact"].includes(userData?.ref_type || ""))
    ) {
      if (defaultPracticeData && defaultPracticeData.data && defaultPracticeData.data.length > 0)
        globalDoctorParams.practice_id = defaultPracticeData.data[0].id
    } else {
      globalDoctorParams.practice_id = treatingPracticeId;
    }

    if (globalDoctorParams.practice_id)
      getGlobalDoctorsListing(null, null, globalDoctorParams);
  }

  /** @second */
  useEffect(() => {
    if (treatingPracticeId && practiceListing) {
      if (firstTreatingPracticeId !== treatingPracticeId) {
        if (userData && ["Practice", "Contact"].includes(userData.ref_type) && !["Treating Provider"].includes(iAm)) {
          resetDoctorAndPatientDetails(setValue);
        }
      }

      if (userData && ["Practice", "Contact"].includes(userData.ref_type)) {
        if (isAnesthesiologist) {
          if (["Treating Provider"].includes(iAm)) {
            getDebouncedDoctorPracListingByQuery("", true)
          } else {
            getDebouncedDoctorPracListingByQuery("_", true)
          }
        } else {
          if (treatingPracticeId && !["Treating Provider"].includes(iAm))
            getGlobalDoctorsListing({}, {}, { practice_id: treatingPracticeId })
        }
      } else {
        getDoctorListing()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatingPracticeId, practiceListing, defaultPracticeData]);

  const getDoctorListingByPracticeId = (pracId: number) => {
    const globalDoctorParams = {
      practice_id: 0,
    };
    if (pracId) {
      globalDoctorParams.practice_id = pracId;
    } else {
      if (defaultPracticeData && defaultPracticeData.data && defaultPracticeData.data.length > 0)
        globalDoctorParams.practice_id = defaultPracticeData.data[0].id
    }

    if (globalDoctorParams.practice_id)
      getGlobalDoctorsListing(null, null, globalDoctorParams);
  }


  useEffect(() => {
    if (userData) {
       if (["Practice", "Contact"].includes(userData.ref_type)) {
        if (["Treating Facility", "Referring and Treating Facility"].includes(iAm)) {
          if (practiceListing && practiceListing.data && practiceListing.data.length > 0) {
            setValue("doctor_attributes.treating_practice_id", practiceListing.data[0].id)
            setValue("shipments_attributes", [{
              practice_id: practiceListing.data[0].id,
              practice_type: practiceListing.data[0].practice_type || "",
              ship_to: "other",
              ship_to_city: practiceListing.data[0].address.city,
              ship_to_state: practiceListing.data[0].address.state,
              ship_to_street: practiceListing.data[0].address.street,
              ship_to_suite: practiceListing.data[0].address.suite,
              ship_to_zip: practiceListing.data[0].address.zip,
              phone: practiceListing.data[0].phone_number ? practiceListing.data[0].phone_number.toString() : "",
              fax: practiceListing.data[0].fax_number ? practiceListing.data[0].fax_number.toString() : "",
            }])
            getDoctorListingByPracticeId(practiceListing.data[0].id)
          }
        } else if (["Treating Provider"].includes(iAm)) {
          getDoctorListing()
        }
      } else if (["Organization"].includes(userData.ref_type)) {
        if (["Treating Provider"].includes(iAm)) {
          getDoctorListing()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceListing, iAm, defaultPracticeData, submittingPractice])


  useEffect(() => {
    if (userData && ["GlobalDoctor"].includes(userData.ref_type))
      if (["Treating Provider"].includes(iAm) && treatingPracticeId) {
        if (globalDoctorsApiResponse && globalDoctorsApiResponse.data && globalDoctorsApiResponse.data.length > 0) {
          setValue("doctor_attributes.treating_doctor_id", globalDoctorsApiResponse.data[0].id)
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDoctorsApiResponse, iAm])




  useEffect(() => {
    if (userData && ["Organization"].includes(userData.ref_type)) {
      if (["Treating Facility"].includes(iAm)) {
        getPracticeListing({}, {}, { practice_id: treatingPracticeId })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatingPracticeId])

  useEffect(() => {
    if (userData && ["Organization"].includes(userData.ref_type)) {
      if (practiceListing && practiceListing.data && practiceListing.data.length > 0) {
        const selectedPrac = practiceListing.data.find((prac) => prac.id === treatingPracticeId)

        if (selectedPrac) {
          setValue("shipments_attributes", [{
            practice_id: selectedPrac.id,
            practice_type: selectedPrac.practice_type || "",
            ship_to: "other",
            ship_to_city: selectedPrac.address.city,
            ship_to_state: selectedPrac.address.state,
            ship_to_street: selectedPrac.address.street,
            ship_to_suite: selectedPrac.address.suite,
            ship_to_zip: selectedPrac.address.zip,
            phone: selectedPrac.phone_number ? selectedPrac.phone_number.toString() : "",
            fax: selectedPrac.fax_number ? selectedPrac.fax_number.toString() : "",
          }])
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceListing])
  // useEffect(() => {
  //   if (iAm && ["Treating Facility", "Referring and Treating Facility"].includes(iAm) && userData && userData.ref_type !== "Organization") {
  //     if (practiceListing && practiceListing.data && practiceListing.data.length > 0) {
  //       const globalDoctorParams = {
  //         practice_id: treatingPracticeId || practiceListing.data[0].id,
  //       };
  //       getGlobalDoctorsListing(null, null, globalDoctorParams);
  //     } else {
  //       getPracticeListing()
  //     }
  //   // } else if (treatingPracticeId) {
  //   //   if (!practiceListing) {
  //   //     getPracticeListing(null, null, { practice_id: treatingPracticeId })
  //   //   }
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [iAm, practiceListing])


  // useEffect(() => {
  //   if (["Treating Facility"].includes(iAm)) {
  //     getPracticeListing()
  //   } else {
  //     clearPracticeListing()
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [iAm])


  // useEffect(() => {
  //   if (globalDoctorsApiResponse && globalDoctorsApiResponse.data && globalDoctorsApiResponse.data.length > 0) {
  //     if (["Treating Provider"].includes(iAm) && userData && userData.ref_id) {
  //       const selectedDoc = globalDoctorsApiResponse.data.find((doc) => doc.id === userData.ref_id)

  //       if (selectedDoc) {
  //         setValue("doctor_attributes.treating_doctor_id", selectedDoc.id)
  //       }
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [globalDoctorsApiResponse])



  return {
    isPracAPILoading, practiceListing, globalDoctorsApiResponse, 
    getPracticeListing: debouncedPracticeSearch, clearPracticeListing,
    getDoctorListingByPracticeId,
    doctorPracLoading,
    doctorPracListing, getDebouncedDoctorPracListingByQuery
  }
}

export default useTreatingPracAndDocFunc
